class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

class CartDrawerUpsell extends HTMLElement {
  triggerVariantID = null;
  triggerVariantQuantity = null;
  upsellVariantID = null;
  mode = null;

  submitButton = null;
  loadingSpinner = null;

  constructor() {
    super()
    
    this.triggerVariantID = this.dataset.triggerVariantId;
    this.triggerVariantQuantity = this.dataset.triggerVariantQuantity;
    this.upsellVariantID = this.dataset.upsellVariantId;
    this.mode = this.dataset.mode ?? 'add';
    this.submitButton = this.querySelector('button');
    this.loadingSpinner = this.querySelector('.loading__spinner');
    
    if (this.triggerVariantID && this.upsellVariantID) {
      this.submitButton.addEventListener('click', this.onClick.bind(this));
    }
  }

  async onClick() {
    this.submitButton.classList.add('loading');
    this.loadingSpinner.classList.remove('hidden');

    const response = this.mode === 'replace' ? await this.replace() : await this.add();
    if (response.status) {
      publish(PUB_SUB_EVENTS.cartError, {
        source: 'cart-drawer-upsell',
        productVariantId: this.upsellVariantID,
        errors: response.errors || response.description,
        message: response.message,
      });
      const errors = document.getElementById('CartDrawer-CartErrors');
      errors.textContent = window.cartStrings.error;
      this.submitButton.classList.remove('loading');
      this.loadingSpinner.classList.add('hidden');
    } else {
      publish(PUB_SUB_EVENTS.cartUpdate, {
        source: 'cart-drawer-upsell',
        productVariantId: this.upsellVariantID,
        cartData: response,
      });
    }
  }

  async add() {
    return fetch(window.Shopify.routes.root + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        items: [
          {
            quantity: 1,
            id: this.upsellVariantID
          }
        ]
      })
    })
    .then((response) => response.json())
    .catch((err) => {
      console.error(err);
      return err
    })
  }

  async replace() {
    let updates = {}
    updates[this.upsellVariantID] = 1
    updates[this.triggerVariantID] = this.triggerVariantQuantity > 1 ? this.triggerVariantQuantity - 1 : 0

    return fetch(window.Shopify.routes.root + 'cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ updates })
    })
    .then((response) => response.json())
    .catch((err) => {
      console.error(err);
      return err
    })
  }
}

customElements.define('cart-drawer-upsell', CartDrawerUpsell);
